import React, { useState } from "react";
import {
    Box,
    Stack,
    Button,
    Chip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import moment from "moment-timezone";
import DrawingDialog from "../../DrawingDialog";

// Helper function to convert decimal to fraction
function decimalToFraction(decimal) {
    if (decimal === null || decimal === undefined || decimal === 0) {
        return "0";
    }

    // Handle negative numbers
    const isNegative = decimal < 0;
    decimal = Math.abs(decimal);

    // If it's already a whole number, return as is
    if (decimal % 1 === 0) {
        return isNegative ? `-${decimal}` : `${decimal}`;
    }

    // Common fractions for typical measurements
    const commonFractions = [
        { decimal: 0.125, fraction: "1/8" },
        { decimal: 0.1875, fraction: "3/16" },
        { decimal: 0.25, fraction: "1/4" },
        { decimal: 0.375, fraction: "3/8" },
        { decimal: 0.5, fraction: "1/2" },
        { decimal: 0.625, fraction: "5/8" },
        { decimal: 0.75, fraction: "3/4" },
        { decimal: 0.875, fraction: "7/8" },
    ];

    // Check for exact matches with common fractions
    const exactMatch = commonFractions.find(
        (f) => Math.abs(f.decimal - decimal) < 0.001
    );
    if (exactMatch) {
        return isNegative ? `-${exactMatch.fraction}` : exactMatch.fraction;
    }

    // Check for mixed numbers (whole number + fraction)
    const wholeNumber = Math.floor(decimal);
    const fractionalPart = decimal - wholeNumber;

    if (wholeNumber > 0) {
        const fractionMatch = commonFractions.find(
            (f) => Math.abs(f.decimal - fractionalPart) < 0.001
        );
        if (fractionMatch) {
            const result = `${wholeNumber} ${fractionMatch.fraction}`;
            return isNegative ? `-${result}` : result;
        }
    }

    // If no common fraction found, use a more general approach
    // Convert to fraction with reasonable denominator
    const tolerance = 0.001;
    for (let denominator = 2; denominator <= 64; denominator++) {
        const numerator = Math.round(decimal * denominator);
        if (Math.abs(decimal - numerator / denominator) < tolerance) {
            // Simplify the fraction
            const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
            const commonDivisor = gcd(numerator, denominator);
            const simplifiedNumerator = numerator / commonDivisor;
            const simplifiedDenominator = denominator / commonDivisor;

            if (simplifiedDenominator === 1) {
                return isNegative
                    ? `-${simplifiedNumerator}`
                    : `${simplifiedNumerator}`;
            }

            const result = `${simplifiedNumerator}/${simplifiedDenominator}`;
            return isNegative ? `-${result}` : result;
        }
    }

    // If all else fails, return the decimal rounded to 3 places
    return isNegative ? `-${decimal.toFixed(3)}` : decimal.toFixed(3);
}

// Window Work Order Dialog
function WindowWorkOrderDialog({
    open,
    onClose,
    order,
    user,
    onMarkChecked,
    onMarkUnchecked,
}) {
    const [openDrawingDialog, setOpenDrawingDialog] = useState(false);
    const [selectedDrawing, setSelectedDrawing] = useState(null);

    if (!order) return null;

    const dueDate = order.due ? moment(order.due).format("MM/DD/YY") : "—";

    // Determine which project target is being used
    const projectLike = order.project || order.service || order.hse || {};
    const projectLabel =
        projectLike.number && projectLike.name
            ? `${projectLike.number} ${projectLike.name}`
            : projectLike.name || projectLike.number || "—";

    const targetType = order.project
        ? "Project"
        : order.service
        ? "Service"
        : order.hse
        ? "HSE"
        : "Unknown";

    const assigneeName = order.assignee
        ? `${order.assignee.first_name || ""} ${order.assignee.last_name ||
              ""}`.trim()
        : "—";
    const drawings = Array.isArray(order.drawings) ? order.drawings : [];

    // Check if user has MANAGEMENT group access
    const isManagement =
        user &&
        user.groups &&
        user.groups.some((group) => group.name === "MANAGEMENT");

    // Checked information
    const checkedByName = order.checked_by
        ? `${order.checked_by.first_name || ""} ${order.checked_by.last_name ||
              ""}`.trim()
        : null;
    const checkedDate = order.checked_at
        ? moment(order.checked_at).format("MM/DD/YY")
        : null;
    const checkedDateDisplay = order.checked
        ? moment(order.checked).format("MM/DD/YY")
        : "—";
    const checkedByDisplay = order.checked_by
        ? order.checked_by.full_name ||
          `${order.checked_by.first_name || ""} ${order.checked_by.last_name ||
              ""}`.trim() ||
          "—"
        : "—";

    const handleOpenDrawing = (d) => {
        const url =
            d.document ||
            d.file_url ||
            d.file ||
            d.url ||
            d.link ||
            d.href ||
            "";
        if (!url) return;
        setSelectedDrawing({ ...d, document: url });
        setOpenDrawingDialog(true);
    };

    // Format dimensions
    const clearDimensions =
        order.clear_width && order.clear_height
            ? `${decimalToFraction(order.clear_width)}" W × ${decimalToFraction(
                  order.clear_height
              )}" H`
            : "—";

    const glassDimensions =
        order.glass_width && order.glass_height
            ? `${decimalToFraction(order.glass_width)}" W × ${decimalToFraction(
                  order.glass_height
              )}" H`
            : "—";

    const frameWidth = order.frame_width
        ? `${decimalToFraction(order.frame_width)}"`
        : "—";

    const glassThickness = order.glass_thickness
        ? `${decimalToFraction(order.glass_thickness)}"`
        : "—";

    return (
        <Dialog fullScreen open={open} disableEscapeKeyDown>
            <DialogTitle>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={{ xs: 2, md: 0 }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    sx={{ position: "relative", pr: { xs: 5, md: 0 } }}
                >
                    <Stack
                        direction="column"
                        spacing={0.5}
                        sx={{ minWidth: 0, flex: 1 }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                minWidth: 0,
                                wordBreak: "break-word",
                                overflow: "hidden",
                                fontWeight: 700,
                            }}
                        >
                            {projectLabel}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 0, wordBreak: "break-word" }}
                        >
                            Work Order: {order.work_order_number || "—"}
                        </Typography>
                        <Box
                            sx={{
                                display: { xs: "flex", md: "none" },
                                gap: 1,
                                mt: 1,
                            }}
                        >
                            {isManagement &&
                                (order.is_complete ? (
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        startIcon={<CancelIcon />}
                                        onClick={() => {
                                            if (onMarkUnchecked) {
                                                onMarkUnchecked(order.id);
                                            }
                                        }}
                                        sx={{
                                            fontSize: {
                                                xs: "0.75rem",
                                                md: "0.875rem",
                                            },
                                            px: { xs: 1, md: 2 },
                                            py: { xs: 0.5, md: 1 },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: {
                                                    xs: "none",
                                                    sm: "inline",
                                                },
                                            }}
                                        >
                                            Mark as Unchecked
                                        </Box>
                                        <Box
                                            sx={{
                                                display: {
                                                    xs: "inline",
                                                    sm: "none",
                                                },
                                            }}
                                        >
                                            Uncheck
                                        </Box>
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={<VerifiedIcon />}
                                        onClick={() => {
                                            if (onMarkChecked) {
                                                onMarkChecked(order.id);
                                            }
                                        }}
                                        sx={{
                                            fontSize: {
                                                xs: "0.75rem",
                                                md: "0.875rem",
                                            },
                                            px: { xs: 1, md: 2 },
                                            py: { xs: 0.5, md: 1 },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: {
                                                    xs: "none",
                                                    sm: "inline",
                                                },
                                            }}
                                        >
                                            Mark as Checked
                                        </Box>
                                        <Box
                                            sx={{
                                                display: {
                                                    xs: "inline",
                                                    sm: "none",
                                                },
                                            }}
                                        >
                                            Check
                                        </Box>
                                    </Button>
                                ))}
                        </Box>
                    </Stack>
                    <Box
                        sx={{
                            display: { xs: "none", md: "flex" },
                            gap: 1,
                            alignItems: "center",
                        }}
                    >
                        {isManagement &&
                            (order.is_complete ? (
                                <Button
                                    variant="contained"
                                    color="warning"
                                    startIcon={<CancelIcon />}
                                    onClick={() => {
                                        if (onMarkUnchecked) {
                                            onMarkUnchecked(order.id);
                                        }
                                    }}
                                    sx={{
                                        fontSize: {
                                            xs: "0.75rem",
                                            md: "0.875rem",
                                        },
                                        px: { xs: 1, md: 2 },
                                        py: { xs: 0.5, md: 1 },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: {
                                                xs: "none",
                                                sm: "inline",
                                            },
                                        }}
                                    >
                                        Mark as Unchecked
                                    </Box>
                                    <Box
                                        sx={{
                                            display: {
                                                xs: "inline",
                                                sm: "none",
                                            },
                                        }}
                                    >
                                        Uncheck
                                    </Box>
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<VerifiedIcon />}
                                    onClick={() => {
                                        if (onMarkChecked) {
                                            onMarkChecked(order.id);
                                        }
                                    }}
                                    sx={{
                                        fontSize: {
                                            xs: "0.75rem",
                                            md: "0.875rem",
                                        },
                                        px: { xs: 1, md: 2 },
                                        py: { xs: 0.5, md: 1 },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: {
                                                xs: "none",
                                                sm: "inline",
                                            },
                                        }}
                                    >
                                        Mark as Checked
                                    </Box>
                                    <Box
                                        sx={{
                                            display: {
                                                xs: "inline",
                                                sm: "none",
                                            },
                                        }}
                                    >
                                        Check
                                    </Box>
                                </Button>
                            ))}
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: { xs: "absolute", md: "static" },
                            top: { xs: -8, md: 0 },
                            right: { xs: -8, md: 0 },
                            color: "inherit",
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ m: 2 }}>
                <Stack spacing={2}>
                    <Stack
                        direction="row"
                        spacing={3}
                        divider={<Divider orientation="vertical" flexItem />}
                    >
                        <Typography>
                            <b>Type:</b> {targetType}
                        </Typography>
                        <Typography>
                            <b>Due:</b> {dueDate}
                        </Typography>
                        <Typography>
                            <b>Assigned To:</b> {assigneeName || "—"}
                        </Typography>
                    </Stack>
                    {order.checked ? (
                        <>
                            <Divider
                                sx={{
                                    borderColor: "success.main",
                                    borderWidth: 1,
                                }}
                            />
                            <Stack
                                direction="row"
                                spacing={3}
                                divider={
                                    <Divider orientation="vertical" flexItem />
                                }
                            >
                                <Typography>
                                    <b>Checked:</b> {checkedDateDisplay}{" "}
                                </Typography>
                                <Typography>
                                    <b>Checked By:</b> {checkedByDisplay}
                                </Typography>
                            </Stack>
                        </>
                    ) : null}

                    <Divider
                        sx={{ borderColor: "success.main", borderWidth: 1 }}
                    />
                    <Typography sx={{ textDecoration: "underline" }}>
                        Window Specifications
                    </Typography>
                    <Stack spacing={1}>
                        <Typography>
                            <b>Quantity:</b> {order.quantity || 0}
                        </Typography>
                        <Typography>
                            <b>Clear Opening:</b> {clearDimensions}
                        </Typography>
                        <Typography>
                            <b>Glass Dimensions:</b> {glassDimensions}
                        </Typography>
                        <Typography>
                            <b>Frame Width:</b> {frameWidth}
                        </Typography>
                    </Stack>

                    <Divider
                        sx={{ borderColor: "success.main", borderWidth: 1 }}
                    />
                    <Typography sx={{ textDecoration: "underline" }}>
                        Glass Specifications
                    </Typography>
                    <Stack spacing={1}>
                        <Typography>
                            <b>Glass Type:</b> {order.glass_type || "—"}
                        </Typography>
                        <Typography>
                            <b>Glass Thickness:</b> {glassThickness}
                        </Typography>
                        <Typography>
                            <b>Glass Both Sides:</b>{" "}
                            {order.glass_both_sides ? "Yes" : "No"}
                        </Typography>
                    </Stack>

                    {order.comments && (
                        <>
                            <Divider
                                sx={{
                                    borderColor: "success.main",
                                    borderWidth: 1,
                                }}
                            />
                            <Typography sx={{ textDecoration: "underline" }}>
                                Comments
                            </Typography>
                            <Typography fontWeight="bold" whiteSpace="pre-wrap">
                                {order.comments}
                            </Typography>
                        </>
                    )}

                    <Divider
                        sx={{ borderColor: "success.main", borderWidth: 1 }}
                    />
                    <Typography sx={{ textDecoration: "underline" }}>
                        Drawings
                    </Typography>
                    <Stack spacing={1}>
                        <div>
                            <Divider sx={{ mt: 2 }} />
                            {drawings.length > 0 ? (
                                <Box textAlign="center">
                                    <List sx={{ mt: 0, pt: 0 }}>
                                        {drawings.map((d) => (
                                            <div key={d.id}>
                                                <ListItem disablePadding>
                                                    <ListItemButton
                                                        onClick={() =>
                                                            handleOpenDrawing(d)
                                                        }
                                                    >
                                                        <ListItemIcon>
                                                            <ArchitectureIcon color="secondary" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={d.title}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                                <Divider />
                                            </div>
                                        ))}
                                    </List>
                                </Box>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 1 }}
                                >
                                    No drawings available
                                </Typography>
                            )}
                        </div>
                    </Stack>
                    <Divider
                        sx={{ borderColor: "success.main", borderWidth: 1 }}
                    />
                    <Stack spacing={1}>
                        <Typography>
                            <b>Created Date:</b>{" "}
                            {order.created
                                ? moment(order.created).format("MM/DD/YY")
                                : "—"}
                        </Typography>
                        <Typography>
                            <b>Created By:</b>{" "}
                            {order.created_by
                                ? `${order.created_by.first_name || ""} ${order
                                      .created_by.last_name || ""}`.trim()
                                : "—"}
                        </Typography>
                        <Typography>
                            <b>Last Updated:</b>{" "}
                            {order.updated
                                ? moment(order.updated).format(
                                      "MM/DD/YY h:mm A"
                                  )
                                : "—"}
                        </Typography>
                        {order.is_complete && (
                            <Typography>
                                <b>Completed At:</b>{" "}
                                {order.completed
                                    ? moment(order.completed).format(
                                          "MM/DD/YY h:mm A"
                                      )
                                    : "—"}
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button variant="contained" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>

            {selectedDrawing && (
                <DrawingDialog
                    openDrawingDialog={openDrawingDialog}
                    setOpenDrawingDialog={setOpenDrawingDialog}
                    drawing={selectedDrawing}
                />
            )}
        </Dialog>
    );
}

export default WindowWorkOrderDialog;
